import { useState, useMemo, useEffect } from "react";
import { useApp } from "../../store.jsx";
import {
  Icon,
  Avatar,
  Pagination,
  EmptyState,
} from "../../components/Common.jsx";
import { AddManagerModal } from "../../components/Modals.jsx";
import {
  listEmployees,
  listDepartments,
  updateEmployee as apiUpdateEmployee,
  deleteEmployee as apiDeleteEmployee,
} from "../../lib/api";

export default function Managers() {
  const { openModal, closeModal, toast } = useApp();
  const [managers, setManagers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("name");
  const [dir, setDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ employees: rows }, depts] = await Promise.all([
        listEmployees(),
        listDepartments().catch(() => []),
      ]);
      const onlyManagers = (rows || []).filter((u) => u.role === "Manager");
      setManagers(onlyManagers);
      setDepartments(Array.isArray(depts) ? depts : []);
    } catch (e) {
      toast(e.message || "Could not load managers", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    let r = [...managers];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(
        (m) =>
          (m.name || "").toLowerCase().includes(q) ||
          (m.email || "").toLowerCase().includes(q) ||
          (m.department || "").toLowerCase().includes(q),
      );
    }
    if (departmentFilter !== "all") {
      r = r.filter((m) => (m.department || "") === departmentFilter);
    }
    if (filter === "active") r = r.filter((m) => m.status === "active");
    else if (filter === "inactive") r = r.filter((m) => m.status === "inactive");
    else if (filter === "whatsapp") r = r.filter((m) => m.whatsapp);
    r.sort((a, b) => {
      const av = a[sort];
      const bv = b[sort];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return r;
  }, [managers, search, filter, departmentFilter, sort, dir]);

  const total = rows.length;
  const start = (page - 1) * pageSize;
  const paged = rows.slice(start, start + pageSize);
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const sortBy = (col) => {
    if (sort === col) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(col);
      setDir("asc");
    }
  };

  const openEditForm = (id) => {
    const m = managers.find((x) => x.id === id);
    if (!m) return;
    const data = {
      name: m.name,
      department: m.department === "—" ? "" : m.department || "",
      phone: m.phone || "",
      isActive: m.status === "active",
    };
    const save = async () => {
      if (!data.name) {
        toast("Name is required", "error");
        return;
      }
      try {
        await apiUpdateEmployee(id, data);
        toast("Manager updated", "success");
        closeModal();
        refresh();
      } catch (err) {
        toast(err.message || "Update failed", "error");
      }
    };
    const remove = async () => {
      if (!window.confirm(`Remove ${m.name}? This cannot be undone.`)) return;
      try {
        await apiDeleteEmployee(id);
        toast("Manager removed", "success");
        closeModal();
        refresh();
      } catch (err) {
        toast(err.message || "Delete failed", "error");
      }
    };
    openModal(
      <>
        <div className="modal-head">
          <h3>Edit manager</h3>
          <button className="icon-btn" onClick={closeModal}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="field">
              <label>Full name</label>
              <input
                type="text"
                className="input"
                defaultValue={data.name}
                onChange={(ev) => (data.name = ev.target.value)}
              />
            </div>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                className="input"
                value={m.email}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label>Phone (WhatsApp)</label>
              <input
                type="tel"
                className="input"
                defaultValue={data.phone}
                onChange={(ev) => (data.phone = ev.target.value)}
              />
            </div>
            <div className="field">
              <label>Department</label>
              <input
                type="text"
                className="input"
                defaultValue={data.department}
                onChange={(ev) => (data.department = ev.target.value)}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label>Status</label>
              <select
                className="input"
                defaultValue={data.isActive ? "active" : "inactive"}
                onChange={(ev) => (data.isActive = ev.target.value === "active")}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-foot">
          <button
            className="btn btn-danger"
            style={{ marginRight: "auto" }}
            onClick={remove}
          >
            <Icon name="trash-2" size={16} /> Remove
          </button>
          <button className="btn btn-ghost" onClick={closeModal}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save}>
            <Icon name="check" size={16} /> Save changes
          </button>
        </div>
      </>,
      "lg",
    );
  };

  const openAddManager = () =>
    openModal(
      <AddManagerModal
        onClose={closeModal}
        onSubmit={(result) => {
          toast(
            `Invitation sent to ${result?.invitation?.email || "manager"}`,
            "success",
          );
          refresh();
        }}
      />,
      "md",
    );

  const setFilterAndReset = (k) => {
    setFilter(k);
    setPage(1);
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Managers</h1>
          <p className="page-sub">
            {total} manager{total === 1 ? "" : "s"} across your company
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-primary btn-sm" onClick={openAddManager}>
            <Icon name="user-plus" size={16} /> Add Manager
          </button>
        </div>
      </div>
      <div className="table-toolbar">
        <div className="toolbar-left">
          <div className="toolbar-search">
            <Icon name="search" size={14} />
            <input
              type="text"
              placeholder="Search by name, email, dept..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          {[
            ["all", "All"],
            ["active", "Active"],
            ["inactive", "Inactive"],
            ["whatsapp", "WhatsApp connected"],
          ].map(([k, l]) => (
            <button
              key={k}
              className={`filter-chip ${filter === k ? "active" : ""}`}
              onClick={() => setFilterAndReset(k)}
            >
              {l}
            </button>
          ))}
          <select
            className="input"
            style={{ height: 30, padding: "0 8px", width: 140, fontSize: 13 }}
            value={departmentFilter}
            onChange={(e) => {
              setDepartmentFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All depts</option>
            {departments.map((d) => (
              <option key={d.id || d.name} value={d.name}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading ? (
        <div className="card" style={{ padding: 24, textAlign: "center" }}>
          Loading managers…
        </div>
      ) : paged.length ? (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input type="checkbox" />
                </th>
                <th onClick={() => sortBy("name")}>
                  Manager{" "}
                  <Icon name="chevrons-up-down" size={11} className="sort-icon" />
                </th>
                <th onClick={() => sortBy("department")}>
                  Department{" "}
                  <Icon name="chevrons-up-down" size={11} className="sort-icon" />
                </th>
                <th>WhatsApp</th>
                <th>Status</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((m) => (
                <tr key={m.id}>
                  <td className="card-hide">
                    <input type="checkbox" />
                  </td>
                  <td data-label="Manager">
                    <div className="cell-emp">
                      <Avatar
                        name={m.name}
                        size="sm"
                        seed={m.id.length + m.name.length}
                      />
                      <div>
                        <div className="name">{m.name}</div>
                        <div className="sub">{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Department">{m.department || "—"}</td>
                  <td data-label="WhatsApp">
                    {m.whatsapp ? (
                      <span
                        style={{
                          color: "#25D366",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <Icon name="check-circle-2" size={14} /> Connected
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>—</span>
                    )}
                  </td>
                  <td data-label="Status">
                    {m.status === "active" ? (
                      <span className="badge badge-success">
                        <span className="badge-dot" /> Active
                      </span>
                    ) : (
                      <span className="badge badge-muted">
                        <span className="badge-dot" /> Inactive
                      </span>
                    )}
                  </td>
                  <td data-label="">
                    <div className="row-actions">
                      <button
                        className="icon-btn"
                        onClick={() => openEditForm(m.id)}
                        title="Edit"
                      >
                        <Icon name="edit-2" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            pages={pages}
            total={total}
            from={start}
            to={Math.min(start + pageSize, total)}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      ) : (
        <EmptyState
          title="No managers found"
          desc={
            managers.length
              ? "No managers match the current filter."
              : "Add your first manager to start assigning teams."
          }
          icon="user-cog"
          cta="Add Manager"
          onCta={openAddManager}
        />
      )}
    </section>
  );
}
